import useAppStore from '@/store/app';
import { useTheme } from '@emotion/react';
import {
  Button,
  Typography,
  Stack,
  Card,
  List,
  Alert,
  ListItemAvatar,
  Avatar,
  ListItemText,
  Paper,
  ListItem,
} from '@mui/material';
import { Box } from '@mui/system';
import ImageGallery from 'react-image-gallery';
import { useEffect, useState } from 'react';
import 'react-image-gallery/styles/css/image-gallery.css';
import EditIcon from '@mui/icons-material/Edit';
import CancelIcon from '@mui/icons-material/Cancel';
import ToggleOnIcon from '@mui/icons-material/ToggleOn';
import InfoIcon from '@mui/icons-material/Info';
import AddIcon from '@mui/icons-material/Add';
import SettingsIcon from '@mui/icons-material/Settings';
import CancelDeployment from '../forms/apps/CancelDeployment';
import tenant from '@/services/TenantService';
import ToastMessage from '../toasts/ToastMessage';
import StandaloneDialog from '../apps/StandaloneDialog';
import DnsInfoDialog from '../apps/DnsInfoDialog';
import ReactivateDeployment from '../forms/apps/ReactivateDeployment';
import useTenantQuery from '@/hooks/useTenantQuery';
import PMAInitialEdit from '../apps/pma/initialEdit';
import AppInstaller from '../apps/Installer';
import SettingsContainer from '../dialogs/Settings/SettingsContainer';
import WeclappInterfaceSettings from '../forms/apps/settings/WeclappInterfaceSettings';
import TanssInterfaceSettings from '../forms/apps/settings/TanssInterfaceSettings';
import TanssErpInterfaceSettings from '../forms/apps/settings/TanssErpInterfaceSettings';
import T2wTemplateSettings from '../forms/apps/settings/T2wTemplateSettings';
import { toast } from 'react-toastify';
import T2wInvoiceSettings from '../forms/apps/settings/T2wInvoiceSettings';
import PassLinkSettings from '../forms/apps/settings/PassLinkSettings';
import RedirectUriSettings from '../forms/apps/settings/RedirectUriSettings';
import GeneralUserSubscriptionsSettings from '../forms/apps/settings/GeneralUSerSubscriptionsSettings';
import TodoConfigSetting from '../forms/apps/settings/TodoConfigSettings';
import WarehousePrinterSettings from '../forms/apps/settings/WarehousePrinterSettings';
import ColorPickerSettings from '../forms/apps/settings/ColorPickerSettings';
import PmaSystemSettings from '../forms/apps/settings/PmaSystemSettings';
import PmaShutdownSettings from '../forms/apps/settings/PmaShutdownSettings';
import AdminRoleSettings from '../forms/apps/settings/AdminRoleSettings';
import T2wAiInterfaceSettings from '../forms/apps/settings/T2wAiInterfaceSettings';
import T2wPromptSettings from '../forms/apps/settings/T2wPromptSettings';
import { useRevalidator } from 'react-router-dom';

export default function ProductPresentation({ product, app, showCard = true }) {
  const { loggedIn } = useAppStore();
  const theme = useTheme();
  const [images, setImages] = useState([]);
  const [selectedDeployment, setSelectedDeployment] = useState(null);
  const [showDeploymentList, setShowDeploymentList] = useState(true);
  const [showEditComponent, setShowEditComponent] = useState(false);
  const [showInstallationComponent, setShowInstallationComponent] =
    useState(false);
  const [showDnsConfig, setShowDnsConfig] = useState(false);
  const [showInitialConfig, setShowInitialConfig] = useState(false);
  const [priceBreakdownOnce, setPriceBreakdownOnce] = useState([]);
  const [priceBreakdownIntervall, setPriceBreakdownIntervall] = useState([]);
  const [deploymentList, setDeploymentList] = useState([]);
  const [reactivable, setReactivable] = useState(
    deploymentList.reduce((acc, deployment) => {
      acc[deployment._id] = deployment.status === 'cancelled';
      return acc;
    }, {}),
  );

  const revalidator = useRevalidator();

  const [editable, setEditable] = useState(
    deploymentList.reduce((acc, deployment) => {
      acc[deployment._id] = deployment.status === 'active';
      return acc;
    }, {}),
  );

  const fetchDeployments = async () => {
    try {
      const response = await tenant.get(`/deployment`);
      let appDeployments;
      if (app) {
        appDeployments = response.data.filter(
          (deployment) =>
            deployment.app === app._id && deployment.expiresAt === undefined,
        );
        setDeploymentList(appDeployments || []);

        setReactivable((prev) => {
          appDeployments.forEach((deployment) => {
            prev[deployment._id] = deployment.status === 'cancelled';
          });
          return { ...prev };
        });
        setEditable((prev) => {
          appDeployments.forEach((deployment) => {
            prev[deployment._id] = deployment.status === 'active';
          });
          return { ...prev };
        });
      }
    } catch (error) {
      console.error('Error fetching deployments:', error);
    }
  };

  useEffect(() => {
    fetchDeployments();
  }, [product]);

  const { setDialogForm } = useAppStore();

  const needsDNSConfig =
    app.deploymentConfig?.properties?.deploymentMode?.value === 'STANDALONE';

  const canHaveMultipleDeployments = ['pass-tool', 'todo-tool', 'pma'].includes(
    app.name,
  );

  const { data: subscriptions } = useTenantQuery('subscription');

  const canBeEdited = [
    'todo-tool',
    'callcenter-tool',
    'callback-tool',
    'warehousenew',
    'pass-tool',
    'pma',
    'tanss2weclapp',
  ].includes(app.name);

  const handleDeploymentSelect = (deployment) => {
    setSelectedDeployment(deployment);
    // setShowDeploymentList(false);
    setShowInstallationComponent(false);
    setShowEditComponent(true);
  };

  const handleInstallation = () => {
    setShowDeploymentList(false);
    setShowEditComponent(false);
    setShowInstallationComponent(true);
  };

  const handleInitialConfig = () => {
    setShowDeploymentList(false);
    setShowEditComponent(false);
    setShowInstallationComponent(false);
    setShowInitialConfig(true);
  };

  const finishInstallation = () => {
    setShowInitialConfig(false);
    setShowDeploymentList(true);
    fetchDeployments();
    setShowEditComponent(false);
    setShowInstallationComponent(false);
  };

  const handleConfirmReactivate = async (deployment) => {
    try {
      await tenant.post(`/deployment/${deployment._id}/reactivate`);
      setReactivable((prev) => ({ ...prev, [deployment._id]: false }));
      setEditable((prev) => ({ ...prev, [deployment._id]: true }));
      toast.success(
        <ToastMessage
          title="Deployment erfolgreich reaktiviert"
          subtitle="Das Deployment wurde erfolgreich reaktiviert."
        />,
      );
      // eslint-disable-next-line no-unused-vars
    } catch (error) {
      toast.error(
        <ToastMessage
          title="Fehler"
          subtitle="Fehler beim Reaktivieren des Deployments"
        />,
      );
    }
    setDialogForm(null);
  };

  const handleReactivate = (deployment) => {
    const deploymentSubscriptions = subscriptions?.filter(
      (sub) => sub.deploymentId === deployment._id,
    );
    setDialogForm(
      <ReactivateDeployment
        deploymentSubscriptions={deploymentSubscriptions}
        deployment={deployment}
        onReactivateSuccess={() => handleConfirmReactivate(deployment)}
        onReactivateFailure={() => setDialogForm(null)}
      />,
    );
  };

  const handleConfirmCancel = async (deployment) => {
    try {
      await tenant.delete(`/deployment/${deployment._id}`);
      setEditable((prev) => ({ ...prev, [deployment._id]: false }));
      setReactivable((prev) => ({ ...prev, [deployment._id]: true }));
      toast.success(
        <ToastMessage
          title="Deployment erfolgreich gekündigt"
          subtitle="Das Deployment wurde erfolgreich gekündigt."
        />,
      );
      // eslint-disable-next-line no-unused-vars
    } catch (error) {
      toast.error(
        <ToastMessage
          title="Fehler"
          subtitle="Fehler beim Kündigen des Deployments"
        />,
      );
    }
    setDialogForm(null);
  };

  const handleCancelSubscription = async (deployment) => {
    setDialogForm(
      <CancelDeployment
        onCancelSuccess={() => handleConfirmCancel(deployment)}
        onCancelFailure={() => setDialogForm(null)}
        deployment={deployment}
      />,
    );
  };

  useEffect(() => {
    if (product && product.image) {
      setImages([
        product.image,
        product.image,
        product.image,
        product.image,
        product.image,
        product.image,
        product.image,
        product.image,
        product.image,
      ]);
    }

    if (product?.priceBreakdown?.length > 0) {
      setPriceBreakdownOnce(
        product?.priceBreakdown?.filter((item) => item.interval === 'one_time'),
      );
      setPriceBreakdownIntervall(
        product?.priceBreakdown?.filter((item) => item.interval !== 'one_time'),
      );
    } else {
      setPriceBreakdownOnce([
        { label: 'DummyPreis', quantity: 1, subtotal: 9999 },
      ]);
    }

    setShowEditComponent(false);
    setShowInstallationComponent(false);
    setShowDeploymentList(true);
    setDeploymentList([]);
  }, [product]);

  const saveEditChanges = (data) => {
    if (!selectedDeployment) return;

    const updatedConfig = {
      ...selectedDeployment.config,
      ...data,
    };

    if (data.tempServiceUnit) {
      selectedDeployment.config.service.unit = data.tempServiceUnit;
      delete data.tempServiceUnit;
    }

    const deploymentData = {
      config: updatedConfig || undefined,
    };

    if ('redirectUri' in data) {
      deploymentData.redirectUri = data.redirectUri;
      deploymentData.config = selectedDeployment?.config || undefined;
    } else if (selectedDeployment?.redirectUri) {
      deploymentData.redirectUri = selectedDeployment.redirectUri;
    }
    tenant
      .put(`/deployment/${selectedDeployment._id}`, deploymentData)
      // eslint-disable-next-line no-unused-vars
      .then((response) => {
        setSelectedDeployment((prev) => ({
          ...prev,
          config: deploymentData.config,
          redirectUri: deploymentData.redirectUri,
        }));
        setEditable((prev) => ({ ...prev, [selectedDeployment._id]: true }));

        const index = deploymentList.findIndex(
          (d) => d._id === selectedDeployment._id,
        );
        if (index !== -1) {
          const updatedDeployments = [...deploymentList];
          updatedDeployments[index] = {
            ...updatedDeployments[index],
            config: deploymentData.config,
            redirectUri: deploymentData.redirectUri,
          };
          setDeploymentList(updatedDeployments);
        }

        revalidator.revalidate();

        toast.success(
          <ToastMessage
            title="Erfolgreich gespeichert"
            subtitle="Die Änderungen wurden gespeichert."
          />,
        );
      })
      // eslint-disable-next-line no-unused-vars
      .catch((error) => {
        toast.error(
          <ToastMessage
            title="Fehler2"
            subtitle="Die Änderungen konnten nicht gespeichert werden."
          />,
        );
      });
  };

  const renderEditComponent = () => {
    if (!selectedDeployment) return null;

    if (app.name === 'pma' && showInitialConfig) {
      return null;
    }

    switch (app.name) {
      case 'todo-tool':
        return [
          {
            key: 'weclapp-Anbindung',
            component: (
              <WeclappInterfaceSettings
                key={'weclapp-Anbindung'}
                app={app}
                deployment={selectedDeployment}
                onSave={saveEditChanges}
              />
            ),
          },
          {
            key: 'TANSS-Anbindung',
            component: (
              <TanssInterfaceSettings
                key={'TANSS-Anbindung'}
                app={app}
                deployment={selectedDeployment}
                onSave={saveEditChanges}
              />
            ),
          },
          {
            key: 'Konfiguration',
            component: (
              <TodoConfigSetting
                deployment={selectedDeployment}
                app={app}
                onSave={saveEditChanges}
              />
            ),
          },
          {
            key: 'Redirect URI',
            component: (
              <RedirectUriSettings
                key="redirectUri"
                deployment={selectedDeployment}
                onSave={saveEditChanges}
              />
            ),
          },
          {
            key: 'Abonnements',
            component: (
              <GeneralUserSubscriptionsSettings
                key="userSubcriptions"
                app={app}
                deployment={selectedDeployment}
              />
            ),
          },
        ];
      case 'callcenter-tool':
        return [
          {
            key: 'Abonnements',
            component: (
              <GeneralUserSubscriptionsSettings
                key="userSubcriptions"
                app={app}
                deployment={selectedDeployment}
              />
            ),
          },
        ];
      case 'callback-tool':
        return [
          {
            key: 'Adminrollen',
            component: (
              <AdminRoleSettings
                key="adminRoles"
                app={app}
                deployment={selectedDeployment}
                onSave={saveEditChanges}
              />
            ),
          },
          {
            key: 'Abonnements',
            component: (
              <GeneralUserSubscriptionsSettings
                key="userSubcriptions"
                app={app}
                deployment={selectedDeployment}
              />
            ),
          },
        ];
      case 'pass-tool':
        return [
          {
            key: 'Farbwahl',
            component: (
              <ColorPickerSettings
                key="themeColor"
                deployment={selectedDeployment}
                app={app}
                onSave={saveEditChanges}
              />
            ),
          },
          {
            key: 'Personalisierung',
            component: (
              <PassLinkSettings
                key="link"
                deployment={selectedDeployment}
                onSave={saveEditChanges}
              />
            ),
          },
          {
            key: 'Redirect URI',
            component: (
              <RedirectUriSettings
                key="redirectUri"
                deployment={selectedDeployment}
                onSave={saveEditChanges}
              />
            ),
          },
          {
            key: 'Abonnements',
            component: (
              <GeneralUserSubscriptionsSettings
                key="userSubcriptions"
                app={app}
                deployment={selectedDeployment}
              />
            ),
          },
        ];
      case 'warehousenew':
        return [
          {
            key: 'weclapp-Anbindung',
            component: (
              <WeclappInterfaceSettings
                key={'weclapp-Anbindung'}
                app={app}
                deployment={selectedDeployment}
                onSave={saveEditChanges}
              />
            ),
          },
          {
            key: 'TANSS-Anbindung',
            component: (
              <TanssInterfaceSettings
                key={'TANSS-Anbindung'}
                app={app}
                deployment={selectedDeployment}
                onSave={saveEditChanges}
              />
            ),
          },
          {
            key: 'Druckerkonfiguration',
            component: (
              <WarehousePrinterSettings
                key={'Druckerkonfiguration '}
                app={app}
                deployment={selectedDeployment}
                onSave={saveEditChanges}
              />
            ),
          },
          {
            key: 'Abonnements',
            component: (
              <GeneralUserSubscriptionsSettings
                key="userSubcriptions"
                app={app}
                deployment={selectedDeployment}
              />
            ),
          },
        ];

      case 'pma':
        return [
          {
            key: 'Systemeinstellungen',
            component: (
              <PmaSystemSettings
                deployment={selectedDeployment}
                app={app}
                onCancel={() => {
                  setShowDeploymentList(true);
                  setShowEditComponent(false);
                }}
                onSave={saveEditChanges}
              />
            ),
          },
          {
            key: 'Shutdown-Plan',
            component: (
              <PmaShutdownSettings
                deployment={selectedDeployment}
                app={app}
                onCancel={() => {
                  setShowDeploymentList(true);
                  setShowEditComponent(false);
                }}
                onSave={saveEditChanges}
              />
            ),
          },
        ];

      case 'tanss2weclapp':
        return [
          {
            key: 'weclapp-Anbindung',
            component: (
              <WeclappInterfaceSettings
                key={'weclapp-Anbindung'}
                app={app}
                deployment={selectedDeployment}
                onSave={saveEditChanges}
              />
            ),
          },
          {
            key: 'TANSS-Anbindung',
            component: (
              <TanssInterfaceSettings
                key={'TANSS-Anbindung'}
                app={app}
                deployment={selectedDeployment}
                onSave={saveEditChanges}
              />
            ),
          },
          {
            key: 'TANSS-ERP-Schnittstelle',
            component: (
              <TanssErpInterfaceSettings
                key={'TANSS-ERP-Schnittstelle'}
                app={app}
                deployment={selectedDeployment}
                onSave={saveEditChanges}
              />
            ),
          },
          {
            key: 'Rechnungskonfiguration',
            component: (
              <T2wTemplateSettings
                key={'Rechnungskonfiguration'}
                deployment={selectedDeployment}
                onSave={saveEditChanges}
              />
            ),
          },
          {
            key: 'Vorlagen für Belege',
            component: (
              <T2wInvoiceSettings
                key={'Vorlagen für Belege'}
                deployment={selectedDeployment}
                onSave={saveEditChanges}
              />
            ),
          },
          {
            key: 'KI-Konfiguration',
            component: (
              <T2wAiInterfaceSettings
                key={'KI-Konfiguration'}
                deployment={selectedDeployment}
                onSave={saveEditChanges}
              />
            ),
          },
          {
            key: 'KI-Prompt Konfiguration',
            component: (
              <T2wPromptSettings
                key={'KI-Prompt Konfiguration'}
                deployment={selectedDeployment}
                onSave={saveEditChanges}
              />
            ),
          },
        ];

      default:
        return (
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <Typography variant="h6">
              Keine Bearbeitungsmöglichkeit für diese App verfügbar
            </Typography>
            <Button
              variant="contained"
              onClick={() => setShowDeploymentList(true)}
              sx={{ mt: 2 }}
            >
              Zurück
            </Button>
          </Box>
        );
    }
  };

  const mainContent = (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '54px',
        [theme.breakpoints.down('md')]: {
          flexDirection: 'column',
        },
        [theme.breakpoints.down('sm')]: {
          gap: '32px',
        },
        width: '100%',
        maxWidth: '1400px',
        height: '100%',
        mr: 8,
      }}
    >
      <Box
        sx={{
          minWidth: '200px',
          width: '40%',
          p: 2,
          pl: 8,
          [theme.breakpoints.down('md')]: {
            width: '100%',
          },
        }}
      >
        <Box
          sx={{
            width: '90%',
          }}
        >
          <ImageGallery
            items={images.map((img) => ({
              original: img,
              thumbnail: img,
            }))}
            showPlayButton={false}
            showFullscreenButton={false}
            thumbnailPosition="bottom"
            showNav={true}
            autoPlay={false}
            showThumbnails={true}
            showIndex={false}
          />
        </Box>
      </Box>

      {showDeploymentList && (
        <Box
          sx={{
            width: '55%',

            [theme.breakpoints.down('md')]: {
              pb: '20%',
            },
            [theme.breakpoints.down('sm')]: {
              width: '100%',
              px: '24px',
            },

            fontWeight: '800',
            fontSize: '48px',
            lineHeight: '1.2',
            [theme.breakpoints.down('sm')]: {
              fontSize: '28px',
            },
          }}
        >
          <Typography
            component="span"
            sx={{
              fontWeight: '800',
              fontSize: '48px',
              lineHeight: '1.2',
              display: 'inline',
              [theme.breakpoints.down('lg')]: {
                fontSize: '36px',
              },
              [theme.breakpoints.down('sm')]: {
                fontSize: '28px',
              },
            }}
          >
            {(() => {
              const parts = product.heading.split(product.title);
              return (
                <>
                  {parts[0]}
                  <Box
                    component="span"
                    sx={{ color: 'primary.main', display: 'inline' }}
                  >
                    {product.title}
                  </Box>
                </>
              );
            })()}
          </Typography>
          <Typography
            sx={{
              mt: '33px',
              fontSize: '16px',
              [theme.breakpoints.down('sm')]: {
                fontSize: '14px',
              },
            }}
          >
            {product.description}
          </Typography>
          {deploymentList?.length === 0 && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="h6" gutterBottom color="primary.main">
                Kostenzusammensetzung
              </Typography>
              {priceBreakdownOnce.length === 1 &&
              priceBreakdownOnce[0].description === 'Einrichtung' &&
              priceBreakdownOnce[0].subtotal === 0 ? (
                <Alert severity="info" sx={{ mb: 2 }}>
                  Nutzen Sie den kostenlosen Probemonat, um alle Funktionen
                  dieses Produkts kennenzulernen. Monatliche Kosten fallen erst
                  nach Ablauf des Probemonats an.
                </Alert>
              ) : (
                <Box>
                  <Typography fontStyle="italic">
                    Einmalig berechnet:{' '}
                  </Typography>
                  {priceBreakdownOnce.map((item, idx) => (
                    <Box
                      key={idx}
                      display="flex"
                      justifyContent="space-between"
                      sx={{ py: 1 }}
                    >
                      <Typography>{item.description}</Typography>
                      <Typography>
                        {(item.subtotal / 100).toFixed(2)} €
                      </Typography>
                    </Box>
                  ))}
                </Box>
              )}

              <Typography fontStyle="italic">Monatlich berechnet: </Typography>

              {priceBreakdownIntervall.map((item, idx) => (
                <Box
                  key={idx}
                  display="flex"
                  justifyContent="space-between"
                  sx={{ py: 1 }}
                >
                  <Typography>{item.description}</Typography>
                  <Typography>{(item.subtotal / 100).toFixed(2)} €</Typography>
                </Box>
              ))}
            </Box>
          )}
          {loggedIn ? (
            deploymentList?.length === 0 && (
              <Stack spacing={2} sx={{ mt: 4 }}>
                <Button
                  variant="outlined"
                  onClick={handleInstallation}
                  size="large"
                  sx={{
                    borderWidth: '2px',
                    borderColor: 'primary.main',
                    boxSizing: 'border-box',
                    borderRadius: 2,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontWeight: '800',
                  }}
                >
                  Jetzt Buchen
                </Button>
              </Stack>
            )
          ) : (
            <Button
              variant="outlined"
              href="/register"
              size="large"
              sx={{
                mt: 2,
                borderWidth: '2px',
                borderColor: 'primary.main',
                boxSizing: 'border-box',
                borderRadius: 2,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontWeight: '800',
              }}
            >
              Jetzt Registrieren
            </Button>
          )}
          {app && deploymentList?.length > 0 && (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                flexGrow: 1,
                height: '100%',
                minHeight: 0,
                mt: 2,
              }}
            >
              <Typography
                variant="h6"
                color="primary"
                fontWeight={500}
                sx={{ pb: 1 }}
              >
                Ihre aktiven Deployments
              </Typography>

              <Box
                sx={{
                  minHeight: '200px',
                  maxHeight: '450px',
                }}
              >
                <List sx={{ overflow: 'auto', maxHeight: '350px', p: 0.1 }}>
                  {deploymentList.map((deployment) => (
                    <Paper
                      key={deployment._id}
                      sx={{
                        mb: 2,
                        borderRadius: 2,
                      }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                        }}
                      >
                        <ListItem
                          sx={{
                            py: 2,
                            flexDirection: {
                              xs: 'column',
                              sm: 'column',
                              md: 'column',
                              lg: 'row',
                            },
                            alignItems: {
                              xs: 'flex-start',
                              sm: 'flex-start',
                              md: 'flex-start',
                              lg: 'center',
                            },
                          }}
                        >
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              width: '100%',
                              mb: { xs: 2, sm: 0 },
                            }}
                          >
                            <ListItemAvatar>
                              <Avatar src={app.icon}>
                                {app.title.charAt(0)}
                              </Avatar>
                            </ListItemAvatar>
                            <ListItemText
                              primary={deployment.title || app.title}
                              secondary={`Erstellt am: ${new Date(deployment.createdAt).toLocaleDateString()}`}
                            />
                          </Box>
                          {app.name === 'pma' &&
                            !deployment.initialSetupFinished && (
                              <Alert
                                severity="warning"
                                sx={{
                                  mb: 2,
                                  width: '100%',
                                }}
                              >
                                Die initiale Konfiguration wurde noch nicht
                                abgeschlossen. Bitte führen Sie diese durch, um
                                das Deployment nutzen zu können.
                              </Alert>
                            )}

                          {needsDNSConfig &&
                            deployment.redirectUri &&
                            deployment.status === 'active' &&
                            !deployment.cloudfrontDistributionId && (
                              <Alert
                                severity="warning"
                                sx={{
                                  mb: 2,
                                  width: '100%',
                                }}
                              >
                                Die DNS-Konfiguration wurde noch nicht
                                abgeschlossen. Bitte führen Sie diese durch, um
                                das Deployment unter <b>{deployment?.domain}</b>{' '}
                                nutzen zu können.
                              </Alert>
                            )}
                          {needsDNSConfig &&
                            !deployment.redirectUri &&
                            deployment.status === 'active' &&
                            !deployment.cloudfrontDistributionId && (
                              <Alert
                                severity="warning"
                                sx={{
                                  mb: 2,
                                  width: '100%',
                                }}
                              >
                                Bitte konfigurieren Sie die URL unter der Ihr
                                Deployment erreichbar sein soll, bevor Sie die
                                DNS Einträge setzen.
                              </Alert>
                            )}

                          <Box
                            sx={{
                              display: 'flex',
                              flexDirection: 'column',
                              gap: 1,
                              width: { xs: '100%', sm: 'auto' },
                              mt: { xs: 1, sm: 0 },
                              ml: { sm: 4 },
                            }}
                          >
                            {canBeEdited && editable[deployment._id] && (
                              <>
                                {app.name === 'pma' ? (
                                  // For PMA apps, show different buttons based on initialSetupFinished
                                  <Box
                                    sx={{
                                      display: 'flex',
                                      flexDirection: 'column',
                                      gap: 1,
                                    }}
                                  >
                                    {deployment.initialSetupFinished && (
                                      <Button
                                        startIcon={<EditIcon />}
                                        variant="outlined"
                                        size="small"
                                        fullWidth
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          // setShowInitialConfig(false);
                                          handleDeploymentSelect(deployment);
                                        }}
                                      >
                                        Bearbeiten
                                      </Button>
                                    )}
                                    <Button
                                      startIcon={<SettingsIcon />}
                                      variant="outlined"
                                      size="small"
                                      fullWidth
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleDeploymentSelect(deployment);
                                        handleInitialConfig();
                                      }}
                                    >
                                      Initiale Konfiguration
                                    </Button>
                                  </Box>
                                ) : (
                                  // For non-PMA apps
                                  <>
                                    {needsDNSConfig &&
                                      deployment.redirectUri &&
                                      deployment.status === 'active' && (
                                        <>
                                          {!deployment.cloudfrontDistributionId ? (
                                            <Button
                                              startIcon={<SettingsIcon />}
                                              variant="outlined"
                                              color="warning"
                                              size="small"
                                              fullWidth
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                setSelectedDeployment(
                                                  deployment,
                                                );
                                                setShowDnsConfig(true);
                                              }}
                                            >
                                              DNS
                                            </Button>
                                          ) : (
                                            <>
                                              <Button
                                                startIcon={<InfoIcon />}
                                                variant="outlined"
                                                size="small"
                                                fullWidth
                                                onClick={(e) => {
                                                  e.stopPropagation();
                                                  setDialogForm(
                                                    <DnsInfoDialog
                                                      deployment={deployment}
                                                      onClose={() =>
                                                        setDialogForm(null)
                                                      }
                                                    />,
                                                  );
                                                }}
                                              >
                                                DNS Info anzeigen
                                              </Button>
                                              <Button
                                                startIcon={<EditIcon />}
                                                variant="outlined"
                                                size="small"
                                                fullWidth
                                                onClick={(e) => {
                                                  e.stopPropagation();
                                                  handleDeploymentSelect(
                                                    deployment,
                                                  );
                                                }}
                                              >
                                                Bearbeiten
                                              </Button>
                                            </>
                                          )}
                                        </>
                                      )}
                                    {/* {!needsDNSConfig && ( */}
                                    <Button
                                      startIcon={<EditIcon />}
                                      variant="outlined"
                                      size="small"
                                      fullWidth
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleDeploymentSelect(deployment);
                                      }}
                                    >
                                      Bearbeiten
                                    </Button>
                                    {/* )} */}
                                  </>
                                )}
                              </>
                            )}
                            {editable[deployment._id] && (
                              <Button
                                color="error"
                                variant="outlined"
                                size="small"
                                fullWidth
                                onClick={() =>
                                  handleCancelSubscription(deployment)
                                }
                                startIcon={<CancelIcon />}
                              >
                                Kündigen
                              </Button>
                            )}
                            {reactivable[deployment._id] && (
                              <Button
                                variant="outlined"
                                size="small"
                                fullWidth
                                onClick={() => handleReactivate(deployment)}
                                startIcon={<ToggleOnIcon />}
                              >
                                Reaktivieren
                              </Button>
                            )}
                          </Box>
                        </ListItem>
                      </Box>
                    </Paper>
                  ))}
                </List>
              </Box>

              <Box sx={{ mt: 2 }}>
                <Box sx={{ display: 'flex', gap: 2 }}>
                  {canHaveMultipleDeployments && (
                    <Button
                      variant="outlined"
                      startIcon={<AddIcon />}
                      onClick={handleInstallation}
                      sx={{
                        borderRadius: 2,
                        borderWidth: '2px',
                        fontWeight: 500,
                        flex: 1,
                      }}
                    >
                      Deployment erstellen
                    </Button>
                  )}
                </Box>
              </Box>
            </Box>
          )}
        </Box>
      )}
      {showEditComponent && (
        <SettingsContainer
          settingsOpen={showEditComponent}
          title="Deployment bearbeiten"
          setSettingsOpen={setShowEditComponent}
          options={renderEditComponent()}
        />
      )}
      {showInstallationComponent && (
        <Box sx={{ height: '90%', width: '55%' }}>
          <AppInstaller
            app={app}
            onCancel={() => {
              setShowInstallationComponent(false);
              setShowDeploymentList(true);
            }}
            onFinish={finishInstallation}
          />
        </Box>
      )}
      {showInitialConfig && (
        <Box sx={{ height: '90%', width: '55%' }}>
          <PMAInitialEdit
            deployment={selectedDeployment}
            app={app}
            onCancel={() => {
              setShowInitialConfig(false);
              setShowDeploymentList(true);
            }}
            onFinish={finishInstallation}
          />
        </Box>
      )}
    </Box>
  );

  if (!showCard) {
    return mainContent;
  }

  return (
    <Box
      style={{ '--primary-main': theme.palette.primary.main }}
      sx={{
        width: '100%',
        height: '100%',
        p: 2,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <Card
        sx={{
          width: '100%',
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          overflow: 'visible',
          [theme.breakpoints.down('sm')]: {
            overflow: 'auto',
          },
        }}
      >
        {mainContent}
      </Card>
      <StandaloneDialog
        open={showDnsConfig}
        onClose={() => {
          setShowDnsConfig(false);
          setShowDeploymentList(true);
        }}
        deployment={selectedDeployment}
        onFinish={() => {
          setShowDnsConfig(false);
          setShowDeploymentList(true);
        }}
      />
    </Box>
  );
}
